import React, { useEffect, useMemo, useState } from "react";
import Table from "../../components/utils/Table";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Button from "../../components/utils/Button";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import CardLayout from "../../components/utils/CardLayout";
import SimListActions from "../../redux/inventory/purchaseSim/actions";
import genericActions from "../../redux/generic/actions";
import moment from "moment";

const { getSimList: fetchSimList } = SimListActions;
const { getVendor, getACode } = genericActions;
const dateFormat = process.env.REACT_APP_DATE_TIME_FORMAT;


const SimList = () => {
  const dispatch = useDispatch();
  const today = new Date();
  const twoDaysBefore = new Date();
  twoDaysBefore.setDate(today.getDate() - 2);
  const formatDate = (date) => date.toISOString().split("T")[0];

  const { simList, getSimListLoading, getSimListError } = useSelector((state) => state.purchaseSim);

  const { vendorList, aCodeList } = useSelector((state) => state.generic);

  const [searchItem, setSearchItem] = useState("");
  const defaultFromDate = formatDate(twoDaysBefore);
  const defaultToDate = formatDate(today);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      networkID: "",
      distributorId: "0",
      purchaseCode: "",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    },
  });

  const filteredItems = useMemo(() => {
    return Array.isArray(simList)
      ? simList.filter(item => {
        if (!searchItem) return true;

        return Object.values(item).some(
          val =>
            typeof val === "string" &&
            val.toLowerCase().includes(searchItem.toLowerCase())
        );
      })
      : [];
  }, [simList, searchItem]);


  const dataSource = filteredItems.map((item, key) => ({
    key: key + 1,
    ACode: item?.CurrentSIMHolder,
    serialNumber: item?.SerialNumber,
    lpa: item?.LPA,
    DealerID: item?.DealerID,
    purchaseNumber: item?.PurchaseNumber,
    createdDate: moment(item.CreatedDateTime).format(dateFormat),
    expireDate: moment(item.ExpireDate).format(dateFormat),
    inventoryStatus: item?.InventoryStatus,
    simType: item?.SIMType,
  }));

  const column = [
    { displayName: "ACode", key: "ACode", },
    { displayName: "Dealer ID", key: "DealerID", },
    { displayName: "Serial Number", key: "serialNumber", },
    { displayName: "LPA", key: "lpa", },
    { displayName: "Purchase Number", key: "purchaseNumber", },
    { displayName: "Purchase Date", key: "createdDate", },
    { displayName: "Expiry Date", key: "expireDate", },
    { displayName: "SIM Status", key: "inventoryStatus", },
    { displayName: "Type", key: "simType", },
  ];

  useEffect(() => {
    dispatch(getVendor());
    dispatch(getACode());
  }, [dispatch]);


  useEffect(() => {

    var selectedNetworkId = 0;
    if (vendorList && vendorList?.length == 1) {
      selectedNetworkId = vendorList[0]?.VendorID
    }

    reset((currentValues) => ({
      ...currentValues,
      networkID: selectedNetworkId,
    }));

    dispatch(
      fetchSimList({
        networkID: Number(selectedNetworkId),
        distributorId: 0,
        purchaseCode: "",
        fromDate: defaultFromDate,
        toDate: defaultToDate,
      })
    );
  }, [vendorList, dispatch, reset]);


  const handleSearch = (data) => {
    data.distributorId = Number(data.distributorId || 0)
    data.networkID = Number(data.networkID || 0)
    dispatch(fetchSimList(data));
  };


  const downloadResponseFile = () => {
    const data = dataSource.map((item, key) => ({
      "ACode": item?.ACode,
      "DealerID": item?.DealerID,
      "Serial Number": item?.serialNumber,
      "LPA":item?.lpa,
      "Purchase Number": item?.purchaseNumber,
      "Created Date Time": moment(item.createdDate).format(dateFormat),
      "Expire Date": moment(item.expireDate).format(dateFormat),
      "SIM Status": item?.inventoryStatus,
      "Type": item?.simType,
    }));

    downloadExcelSampleFile(data, "sim-list.xlsx");
  };

  return (
    <CardLayout title="SIM List" icon="ri-sim-card-line "
      rightContent={
        <div className="d-flex flex-wrap align-items-center gap-1">
          <input
            type="search"
            style={{ width: "300px", }}
            className="form-control"
            placeholder="Search..."
            onChange={(e) => setSearchItem(e.target.value)}
          />
          <Button
            title="Excel"
            buttonType="button"
            variant="secondary"
            icon={<i className="ri-download-cloud-line me-2"></i>}
            onClick={downloadResponseFile}
          />
        </div>
      }>
      <div className="row mb-3">
        {/* Search From*/}
        <form onSubmit={handleSubmit(handleSearch)}>
          <div className="row">

            {/* ACode */}
            <div className="form-group col-lg-2 col-md-4 col-12 col-sm-6">
              <label className="form-label fw-semibold">
                ACode
              </label>

              <select
                className={`form-select`}
                {...register("distributorId",)}
              >
                <option value="0">All </option>
                {aCodeList?.map((item) => (
                  <option key={item?.Id} value={item?.Id}>
                    {item?.ACode}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-lg-2 col-md-4 col-12 col-sm-6">
              <label className="form-label fw-semibold">
                Network
              </label>
              <select
                className={`form-select`}
                // disabled
                {...register("networkID")}
              >
                <option value="">Select Network</option>
                {vendorList?.map((item) => (
                  <option key={item.VendorID} value={item.VendorID}>
                    {item.VendorName}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div className="form-group col-lg-3 col-md-4 col-12 col-sm-6">
              <label className="form-label fw-semibold">
                From Date <span className="text-danger">*</span>
              </label>

              <input
                type="date"
                className={`form-control ${errors.fromDate ? "is-invalid" : ""}`}
                {...register("fromDate", {
                  required: "From date is required",
                })}
              />

              {errors.fromDate && <div className="invalid-feedback"> {errors.fromDate.message} </div>}
            </div>

            {/* To Date */}
            <div className="form-group col-lg-3 col-md-4 col-12 col-sm-6">
              <label className="form-label fw-semibold">
                To Date <span className="text-danger">*</span>
              </label>

              <input
                type="date"
                className={`form-control ${errors.toDate ? "is-invalid" : ""}`}
                {...register("toDate", {
                  required: "To date is required",
                })}
              />
              {errors.toDate && <div className="invalid-feedback"> {errors.toDate.message} </div>}

            </div>

            <div className="col-md-2 d-flex align-items-end" style={{ height: '70px' }}>
              <Button
                title="Get Report"
                buttonType="submit"
                variant="primary"
                icon={<i className="ri-search-line me-2"></i>}
                loading={getSimListLoading}
                disable={getSimListLoading}
                style={getSimListLoading ? { width: '70%', padding: '10px 2px' } : {}}
              />
            </div>

          </div>

        </form>
      </div>
      {/* Table */}
      <div>
        <Table
          columns={column}
          loading={getSimListLoading}
          data={dataSource}
          pagination={true}
          tableHeight="325px"
        />
      </div>
    </CardLayout>
  );
};
export default SimList;
